import React, { useEffect, useMemo, useRef, useState } from 'react';
import { scopeNoun } from '../../../../utils/trustScope';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ShieldCheck, RefreshCw, Eye, Shield, Lock, Unlock,
  Activity, TrendingDown, TrendingUp,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, TrustChip, Sparkline } from '../../index';
import { ProbationModal, AutonomyOverrideModal } from '../modals';
import {
  getTrustProfilesToWatch, getAgentScoreTrends,
  triggerProbation, releaseProbation, manualAutonomyOverride,
} from '../../../../services/api';
import { bandTone } from '../../../../services/trustBands';

/**
 * AnomaliesTab — V2 Anomalies & Alerts (Phase A6 / G306).
 *
 * Full V1 feature parity:
 *   - Two-column layout: alert list (350px) | detail panel (1fr)
 *   - Auto-refresh toggle (30s interval)
 *   - Anomaly type badges (SCORE_DRIFT / BLOCK_SPIKE / UNUSUAL_PATTERN)
 *   - Confidence percentage
 *   - Selection accent line + light background
 *   - Detail: key info grid + description + 30-day score history sparkline + inline actions
 *   - Empty state ("All Clear") when nothing flagged
 *
 * Scope-aware: filters /trust/profiles/watch by the current
 * scope-filtered agent population. Inline Probation / Override
 * actions live in the detail panel so the operator can take action
 * without leaving the tab.
 *
 * @param {Object} props
 * @param {string} props.scope           — a key from utils/trustScope (all / let / autolet / extelet / syslet)
 * @param {Array} props.enriched         — scope-filtered agents (for filtering watch list)
 * @param {Function} props.openSlideout
 * @param {Function} props.onAgentUpdated
 * @param {Function} props.addToast
 */
/**
 * G327 — Robust response normaliser for apiFetch + Spring Pageable.
 * See TrustTimelineTab.extractList for full design rationale.
 */
function extractList(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  const d = r.data;
  if (Array.isArray(d)) return d;
  for (const k of ['items', 'content', 'results', 'profiles', 'points', 'events']) {
    if (Array.isArray(r[k])) return r[k];
  }
  if (d && typeof d === 'object') {
    for (const k of ['content', 'items', 'results', 'profiles', 'points', 'events']) {
      if (Array.isArray(d[k])) return d[k];
    }
  }
  return [];
}

export function AnomaliesTab({ scope, enriched, openSlideout, onAgentUpdated, addToast }) {
  const navigate = useNavigate();
  const [watch, setWatch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const timerRef = useRef(null);

  /* Per-selected-agent score history. Lazy-loaded so we don't fan
   * out to 50+ trends endpoints when the page first loads. */
  const [trendByAgent, setTrendByAgent] = useState({});

  /* Action modal state — reuses the same modals as InventoryTab so
   * UX is identical across tabs. */
  const [probationModal, setProbationModal] = useState(null);
  const [overrideModal, setOverrideModal] = useState(null);

  const loadWatch = async () => {
    setLoading(true);
    try {
      const r = await getTrustProfilesToWatch();
      /* G327 — same robust extractor pattern as TrustTimelineTab.
       * apiFetch wraps as { ok, data }, and Pageable endpoints wrap
       * data further as { content, totalElements }. */
      setWatch(extractList(r));
      setRefreshedAt(new Date());
    } catch (_) {
      setWatch([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadWatch(); /* eslint-disable-next-line */ }, []);

  /* Auto-refresh — polling pattern. The server doesn't push, so we
   * pull every 30s when the toggle is on. Cleared on toggle off,
   * component unmount, or tab switch. */
  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    timerRef.current = setInterval(loadWatch, 30_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh]);

  /* Filter the watch list to the current scope. The server returns the tenant-wide list; what is
   * in scope is exactly what survived into `enriched`, which the shell has already filtered.
   *
   * This used to re-derive the scope here against its own archetype tests, which meant two places
   * decided what a scope meant — and when the shell gained Lets and AutoLets as separate chips,
   * this copy stopped recognising any of the keys and quietly showed the whole fleet under every
   * filter. Membership in `enriched` is the one test. */
  const filtered = useMemo(() => {
    if (scope === 'all') return watch;
    return watch.filter((p) =>
      enriched.some((a) => a.id === p.agentId || a.id === p.id));
  }, [watch, scope, enriched]);

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return filtered.find((p) => (p.agentId || p.id) === selectedId) || null;
  }, [filtered, selectedId]);

  /* Resolve the enriched agent record for the selection so we have
   * trust score, archetype, autonomy etc. for the detail panel. */
  const selectedAgent = useMemo(() => {
    if (!selected) return null;
    const id = selected.agentId || selected.id;
    return enriched.find((a) => a.id === id) || null;
  }, [selected, enriched]);

  /* Lazy-load score trend when an item is selected. Cache so
   * re-selecting doesn't re-fetch. */
  useEffect(() => {
    if (!selected) return;
    const id = selected.agentId || selected.id;
    if (!id || trendByAgent[id]) return;
    let cancelled = false;
    getAgentScoreTrends(id, 30)
      .then((r) => {
        if (cancelled) return;
        const points = extractList(r);
        const series = points.map((p) => Number(p.score)).filter(Number.isFinite);
        setTrendByAgent((m) => ({ ...m, [id]: series }));
      })
      .catch(() => !cancelled && setTrendByAgent((m) => ({ ...m, [id]: [] })));
    return () => { cancelled = true; };
  }, [selected, trendByAgent]);

  /* ── Mutation handlers ──────────────────────────────────────── */
  const handleProbation = async ({ reason, expiresAt }) => {
    const { mode, agent } = probationModal;
    if (mode === 'place') {
      const r = await triggerProbation(agent.id, reason, expiresAt);
      if (r?.ok === false) throw new Error(r.error || 'Failed');
      addToast?.(`${agent.name} placed on probation.`, 'success');
    } else {
      const r = await releaseProbation(agent.id, reason);
      if (r?.ok === false) throw new Error(r.error || 'Failed');
      addToast?.(`${agent.name} released from probation.`, 'success');
    }
    onAgentUpdated?.();
    loadWatch();
  };

  const handleOverride = async ({ newLevel, reason }) => {
    const r = await manualAutonomyOverride(overrideModal.id, newLevel, reason);
    if (r?.ok === false) throw new Error(r.error || 'Failed');
    addToast?.(`Autonomy override applied to ${overrideModal.name}.`, 'success');
    onAgentUpdated?.();
    loadWatch();
  };

  /* ── Render ─────────────────────────────────────────────────── */
  const onProbationActive = selectedAgent?.probationUntil
    || selectedAgent?.autonomy === 'PROBATION';

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">Anomalies &amp; alerts</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-0.5 m-0">
            Anomalies, probation, and trust regressions — auto-detected by the scoring engine.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-[12px] text-v2-ink-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (30s)
          </label>
          <Button variant="ghost" size="sm"
                  iconLeft={<RefreshCw size={13} className={loading ? 'animate-spin' : ''} />}
                  onClick={loadWatch} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: '350px 1fr' }}>
        {/* Alert list */}
        <Card padding="none" className="flex flex-col" style={{ maxHeight: 720, overflow: 'hidden' }}>
          <div className="px-4 py-3 border-b border-v2-border-subtle bg-v2-surface-muted flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted">
              {filtered.length} flagged
            </span>
            {refreshedAt && (
              <span className="text-[10.5px] text-v2-ink-muted font-mono-v2">
                {refreshedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && filtered.length === 0 && (
              <p className="text-v2-ink-muted text-[12.5px] p-4">Scanning…</p>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-6 text-center">
                <ShieldCheck size={28} className="text-v2-success mx-auto mb-2" />
                <div className="text-[13px] font-semibold text-v2-ink">All clear</div>
                <p className="text-[11.5px] text-v2-ink-muted mt-1 m-0">
                  {`No anomalies in ${scopeNoun(scope)}.`}
                </p>
              </div>
            )}
            {filtered.length > 0 && (
              <ul className="m-0 p-0 list-none">
                {filtered.map((p, i) => {
                  const id = p.agentId || p.id;
                  const isSelected = id === selectedId;
                  const agent = enriched.find((a) => a.id === id);
                  const sev = p.severity || (p.trustScore != null && p.trustScore < 60 ? 'high' : 'medium');
                  return (
                    <li key={id || i}
                        onClick={() => setSelectedId(id)}
                        className={`px-4 py-3 cursor-pointer border-b border-v2-border-subtle/60 ${
                          isSelected
                            ? 'bg-v2-action-soft border-l-4 border-l-v2-action'
                            : 'border-l-4 border-l-transparent hover:bg-v2-surface-muted'
                        }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-v2-ink text-[13px] truncate flex-1">
                          {p.agentName || agent?.name || id}
                        </span>
                        <Pill tone={sev === 'high' ? 'danger' : 'warning'} size="sm" dot>
                          {sev}
                        </Pill>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] text-v2-ink-muted">
                        <AlertTriangle size={11} className="text-v2-warning shrink-0" />
                        <span className="truncate flex-1">
                          {p.anomalyType
                            ? String(p.anomalyType).replace(/_/g, ' ')
                            : (p.reason || p.flag || p.message || 'Flagged for review')}
                        </span>
                        {p.confidence != null && (
                          <span className="font-mono-v2 text-v2-ink-muted">
                            {Math.round(p.confidence * 100)}%
                          </span>
                        )}
                      </div>
                      {p.detectedAt && (
                        <div className="text-[10.5px] text-v2-ink-muted mt-0.5 font-mono-v2">
                          {new Date(p.detectedAt).toLocaleString()}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>

        {/* Detail panel */}
        <Card padding="md" className="min-h-[400px]">
          {!selected && (
            <div className="flex flex-col items-center justify-center text-center py-16">
              <Eye size={32} className="text-v2-ink-muted mb-3" />
              <h3 className="text-[15px] font-semibold text-v2-ink m-0">Select an alert</h3>
              <p className="text-[12.5px] text-v2-ink-muted mt-1 max-w-sm">
                Pick a flagged agent from the list to view anomaly details, score history, and take action.
              </p>
            </div>
          )}
          {selected && (
            <>
              {/* Header */}
              <div className="flex items-start gap-3 mb-4 pb-4 border-b border-v2-border-subtle">
                <div className="w-11 h-11 rounded-v2-md bg-v2-warning-soft flex items-center justify-center shrink-0">
                  <Shield size={20} className="text-v2-warning-strong" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] font-bold text-v2-ink m-0 truncate">
                    {selected.agentName || selectedAgent?.name || selected.agentId}
                  </h3>
                  <div className="text-[11.5px] text-v2-ink-muted mt-0.5">
                    {selectedAgent?.archetype || '—'}
                    {selectedAgent?.autonomy && <> · {selectedAgent.autonomy.replace(/_/g, ' ')}</>}
                    {selected.detectedAt && <> · detected {new Date(selected.detectedAt).toLocaleString()}</>}
                  </div>
                </div>
                {selectedAgent?.trust != null && (
                  <TrustChip score={selectedAgent.trust} autonomy={selectedAgent.autonomy} />
                )}
              </div>

              {/* Key info grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                <KeyCell label="Trust score" value={selectedAgent?.trust ?? '—'} highlight />
                <KeyCell
                  label="Confidence"
                  value={selected.confidence != null ? `${Math.round(selected.confidence * 100)}%` : '—'}
                />
                <KeyCell
                  label="Risk level"
                  value={selectedAgent?.trust >= 90 ? 'LOW'
                       : selectedAgent?.trust >= 80 ? 'MEDIUM'
                       : selectedAgent?.trust >= 70 ? 'HIGH'
                       : 'CRITICAL'}
                />
              </div>

              {/* Description */}
              <div className="bg-v2-warning-soft border border-v2-warning/30 rounded-v2-md p-3 mb-4 text-[12.5px] text-v2-ink">
                <span className="font-semibold text-v2-warning-strong block mb-1 text-[11px] uppercase tracking-wider">
                  <AlertTriangle size={11} className="inline -mt-0.5 mr-1" />
                  {selected.anomalyType ? String(selected.anomalyType).replace(/_/g, ' ') : 'Flagged'}
                </span>
                {selected.description || selected.reason || selected.message
                  || (selected.anomalyType === 'SCORE_DRIFT'
                      ? "Agent's trust score is drifting outside acceptable thresholds. Review recent evaluations."
                      : selected.anomalyType === 'BLOCK_SPIKE'
                        ? 'Policy block rate has spiked. Investigate whether recent skills or prompts are triggering new violations.'
                        : selected.anomalyType === 'UNUSUAL_PATTERN'
                          ? "Behavioural pattern deviates from the agent's established baseline."
                          : 'Agent flagged by the scoring engine — investigate to determine appropriate action.')}
              </div>

              {/* Score history sparkline */}
              <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">
                    30-day trust score
                  </span>
                  {(() => {
                    const series = trendByAgent[selected.agentId || selected.id];
                    if (!series || series.length < 2) return null;
                    const delta = Math.round(series[series.length - 1] - series[0]);
                    return (
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                        delta > 0 ? 'text-v2-success-strong'
                        : delta < 0 ? 'text-v2-danger-strong'
                        : 'text-v2-ink-muted'
                      }`}>
                        {delta > 0 ? <TrendingUp size={12} /> : delta < 0 ? <TrendingDown size={12} /> : null}
                        {delta > 0 ? '+' : ''}{delta} pts
                      </span>
                    );
                  })()}
                </div>
                {(() => {
                  const series = trendByAgent[selected.agentId || selected.id];
                  if (series === undefined) {
                    return <p className="text-[11.5px] text-v2-ink-muted m-0">Loading score history…</p>;
                  }
                  if (series.length < 2) {
                    return <p className="text-[11.5px] text-v2-ink-muted italic m-0">No score history yet.</p>;
                  }
                  const last = series[series.length - 1];
                  return (
                    <Sparkline
                      data={series}
                      tone={bandTone(last)}
                      height={48}
                    />
                  );
                })()}
              </div>

              {/* Inline actions */}
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant={onProbationActive ? 'secondary' : 'danger'}
                  size="sm"
                  iconLeft={onProbationActive ? <Unlock size={13} /> : <Lock size={13} />}
                  onClick={() => selectedAgent && setProbationModal({
                    mode: onProbationActive ? 'release' : 'place', agent: selectedAgent,
                  })}
                  disabled={!selectedAgent}
                >
                  {onProbationActive ? 'Release probation' : 'Place on probation'}
                </Button>
                <Button variant="secondary" size="sm" iconLeft={<Shield size={13} />}
                        onClick={() => selectedAgent && setOverrideModal(selectedAgent)}
                        disabled={!selectedAgent}>
                  Override autonomy
                </Button>
                <Button variant="ghost" size="sm" iconLeft={<Activity size={13} />}
                        onClick={() => selectedAgent && openSlideout?.(selectedAgent)}
                        disabled={!selectedAgent}>
                  Open trust detail
                </Button>
                <Button variant="ghost" size="sm" iconLeft={<Eye size={13} />}
                        onClick={() => navigate(`/agents/${selected.agentId || selected.id}`)}>
                  Agent page
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      {probationModal && (
        <ProbationModal
          isOpen
          mode={probationModal.mode}
          agent={probationModal.agent}
          onClose={() => setProbationModal(null)}
          onSubmit={handleProbation}
        />
      )}
      {overrideModal && (
        <AutonomyOverrideModal
          isOpen
          agent={overrideModal}
          onClose={() => setOverrideModal(null)}
          onSubmit={handleOverride}
        />
      )}
    </>
  );
}

function KeyCell({ label, value, highlight = false }) {
  return (
    <div className={`bg-v2-surface-muted rounded-v2-sm p-2.5 ${highlight ? 'border-l-2 border-v2-action' : ''}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted">{label}</div>
      <div className="text-[16px] font-bold text-v2-ink mt-0.5">{value}</div>
    </div>
  );
}

export default AnomaliesTab;
